import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Typography,
  Box,
  Divider,
  Avatar,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Snackbar,
  CircularProgress,
  useMediaQuery,
} from '@mui/material';
import { Share, ContentCopy, Close, PictureAsPdf } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { en } from '../translations/en';
import { useAuthorization } from '../hooks/useAuthorization';
import { getPublicCVData, PublicCVData } from '../utils/cvApi';
import { BrandIconLink } from '../components/cv/BrandIconLink';
import { mergeBrandEntries } from '../components/cv/mergeBrandEntries';
import { renderSafeMarkdown } from '../utils/safeMarkdown';

export const PublicCV = () => {
  const theme = useTheme();
  const fullScreenMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { aliId } = useParams<{ aliId: string }>();
  const [searchParams] = useSearchParams();
  const showInfoBar = searchParams.get('from') === 'cv';
  const { isCommercialTier } = useAuthorization();
  const isCommercial = isCommercialTier();

  // State for CV data
  const [cvData, setCvData] = useState<PublicCVData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const brandEntries = useMemo(() => (cvData ? mergeBrandEntries(cvData) : []), [cvData]);

  // Share modal state
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const shareUrl = window.location.href;

  // Fetch public CV data on mount
  useEffect(() => {
    const fetchPublicCV = async () => {
      if (!aliId) {
        setError('No ALI ID provided');
        setLoading(false);
        return;
      }

      try {
        const data = await getPublicCVData(aliId);
        setCvData(data);
        setError(null);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load CV';
        setError(errorMessage === 'CV not found' ? 'Profile not found' : 'Failed to load profile');
      } finally {
        setLoading(false);
      }
    };

    fetchPublicCV();
  }, [aliId]);

  const handleShare = () => {
    setShareModalOpen(true);
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    setSnackbarMessage('Link copied to clipboard!');
    setSnackbarOpen(true);
    setShareModalOpen(false);
  };

  // Get initials from name
  const getInitials = (name?: string): string => {
    if (!name) return '?';
    const parts = name.split(' ').filter(Boolean);
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return parts[0]?.[0]?.toUpperCase() || '?';
  };

  // Check if CV has any public content
  const hasPublicContent = cvData && (
    cvData.biography ||
    cvData.additionalInfo ||
    cvData.cvPdfUrl ||
    cvData.logoUrl ||
    (cvData.education && cvData.education.length > 0) ||
    (cvData.soloExhibitions && cvData.soloExhibitions.length > 0) ||
    (cvData.groupExhibitions && cvData.groupExhibitions.length > 0) ||
    (cvData.awards && cvData.awards.length > 0) ||
    (cvData.publications && cvData.publications.length > 0) ||
    (cvData.contact && cvData.contact.length > 0) ||
    (cvData.collections && cvData.collections.length > 0) ||
    (cvData.galleryRepresentation && cvData.galleryRepresentation.length > 0) ||
    (cvData.socialMedia && cvData.socialMedia.length > 0)
  );

  // Loading state
  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // Error state
  if (error) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ py: 4 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      </Container>
    );
  }

  // No data state (shouldn't happen if API works correctly)
  if (!cvData) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ py: 4 }}>
          <Alert severity="warning">No CV data available</Alert>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{
        py: { xs: 2, md: 4 },
        px: { xs: 2, md: 0 }
      }}>
        {/* Info Bar - shown when coming from CV page */}
        {showInfoBar && (
          <Alert
            severity="info"
            sx={{ mb: 3 }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => navigate('/curriculum-vitae')}
              >
                Cancel
              </Button>
            }
          >
            This content about your CV is: <strong>Public</strong>
          </Alert>
        )}

        {/* Title outside container */}
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h1" component="h1" sx={{ color: theme.palette.text.primary }}>
            {isCommercial ? en.profile.commercial.about : en.profile.curriculumVitae}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {cvData.cvPdfUrl && (
              <Button
                variant="contained"
                startIcon={<PictureAsPdf />}
                component="a"
                href={cvData.cvPdfUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Download CV
              </Button>
            )}
            <Button variant="outlined" startIcon={<Share />} onClick={handleShare}>
              Share
            </Button>
          </Box>
        </Box>

        <Box sx={{
          backgroundColor: theme.palette.background.paper,
          borderRadius: 2,
          p: 4,
          boxShadow: theme.shadows[1]
        }}>
          {/* Profile Section */}
          <Box sx={{
            display: 'flex',
            gap: 4,
            mb: 4,
            alignItems: 'flex-start'
          }}>
            {cvData.logoUrl ? (
              <Box
                component="img"
                src={cvData.logoUrl}
                alt="Artist Logo"
                sx={{
                  width: 120,
                  height: 120,
                  objectFit: 'contain',
                  borderRadius: 1,
                }}
              />
            ) : (
              <Avatar
                src={cvData.profileImageUrl || undefined}
                sx={{
                  width: 120,
                  height: 120,
                  fontSize: '3rem',
                  bgcolor: theme.palette.primary.main
                }}
              >
                {getInitials(cvData.name)}
              </Avatar>
            )}
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="h4" sx={{ color: theme.palette.text.primary }}>
                  {cvData.name || ''}
                </Typography>
              </Box>
              <Typography variant="body1" sx={{ color: theme.palette.text.secondary, mb: 1 }}>
                ALI ID: {cvData.aliId}
              </Typography>
              {brandEntries.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1, ml: -1 }}>
                  {brandEntries.map((entry, idx) => (
                    <BrandIconLink key={`${entry.type}-${idx}`} type={entry.type} value={entry.value} />
                  ))}
                </Box>
              )}
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Biography / About Us */}
          {cvData.biography && (
            <>
              <Box sx={{ mb: 4 }}>
                <Typography variant="h5" sx={{ mb: 2, color: theme.palette.text.primary }}>
                  {isCommercial ? en.profile.commercial.aboutUs : 'Biography'}
                </Typography>
                <Typography variant="body1" sx={{ color: theme.palette.text.secondary, whiteSpace: 'pre-wrap' }}>
                  {renderSafeMarkdown(cvData.biography)}
                </Typography>
              </Box>
              <Divider sx={{ my: 3 }} />
            </>
          )}

          {/* Creator-only sections - hidden for commercial tier */}
          {!isCommercial && (
            <>
              {/* Education */}
              {cvData.education && cvData.education.length > 0 && (
                <>
                  <Box sx={{ mb: 4 }}>
                    <Typography variant="h5" sx={{ mb: 2, color: theme.palette.text.primary }}>
                      Education
                    </Typography>
                    {cvData.education.map((edu, index) => (
                      <Typography key={index} variant="body1" sx={{ color: theme.palette.text.secondary, mb: 1 }}>
                        {renderSafeMarkdown(edu.text)}
                      </Typography>
                    ))}
                  </Box>
                  <Divider sx={{ my: 3 }} />
                </>
              )}

              {/* Solo Exhibitions */}
              {cvData.soloExhibitions && cvData.soloExhibitions.length > 0 && (
                <>
                  <Box sx={{ mb: 4 }}>
                    <Typography variant="h5" sx={{ mb: 2, color: theme.palette.text.primary }}>
                      Solo Exhibitions
                    </Typography>
                    {cvData.soloExhibitions.map((exhibition, index) => (
                      <Typography key={index} variant="body1" sx={{ color: theme.palette.text.secondary, mb: 1 }}>
                        {renderSafeMarkdown(exhibition.text)}
                      </Typography>
                    ))}
                  </Box>
                  <Divider sx={{ my: 3 }} />
                </>
              )}

              {/* Group Exhibitions */}
              {cvData.groupExhibitions && cvData.groupExhibitions.length > 0 && (
                <>
                  <Box sx={{ mb: 4 }}>
                    <Typography variant="h5" sx={{ mb: 2, color: theme.palette.text.primary }}>
                      Group Exhibitions
                    </Typography>
                    {cvData.groupExhibitions.map((exhibition, index) => (
                      <Typography key={index} variant="body1" sx={{ color: theme.palette.text.secondary, mb: 1 }}>
                        {renderSafeMarkdown(exhibition.text)}
                      </Typography>
                    ))}
                  </Box>
                  <Divider sx={{ my: 3 }} />
                </>
              )}

              {/* Awards */}
              {cvData.awards && cvData.awards.length > 0 && (
                <>
                  <Box sx={{ mb: 4 }}>
                    <Typography variant="h5" sx={{ mb: 2, color: theme.palette.text.primary }}>
                      Awards
                    </Typography>
                    {cvData.awards.map((award, index) => (
                      <Typography key={index} variant="body1" sx={{ color: theme.palette.text.secondary, mb: 1 }}>
                        {renderSafeMarkdown(award.text)}
                      </Typography>
                    ))}
                  </Box>
                  <Divider sx={{ my: 3 }} />
                </>
              )}

              {/* Publications */}
              {cvData.publications && cvData.publications.length > 0 && (
                <>
                  <Box sx={{ mb: 4 }}>
                    <Typography variant="h5" sx={{ mb: 2, color: theme.palette.text.primary }}>
                      Publications
                    </Typography>
                    {cvData.publications.map((pub, index) => (
                      <Typography key={index} variant="body1" sx={{ color: theme.palette.text.secondary, mb: 1 }}>
                        {renderSafeMarkdown(pub.text)}
                      </Typography>
                    ))}
                  </Box>
                  <Divider sx={{ my: 3 }} />
                </>
              )}

              {/* Collections */}
              {cvData.collections && cvData.collections.length > 0 && (
                <>
                  <Box sx={{ mb: 4 }}>
                    <Typography variant="h5" sx={{ mb: 2, color: theme.palette.text.primary }}>
                      Collections
                    </Typography>
                    {cvData.collections.map((collection, index) => (
                      <Typography key={index} variant="body1" sx={{ color: theme.palette.text.secondary, mb: 1 }}>
                        {renderSafeMarkdown(collection.name)}
                      </Typography>
                    ))}
                  </Box>
                  <Divider sx={{ my: 3 }} />
                </>
              )}

              {/* Gallery Representation */}
              {cvData.galleryRepresentation && cvData.galleryRepresentation.length > 0 && (
                <>
                  <Box sx={{ mb: 4 }}>
                    <Typography variant="h5" sx={{ mb: 2, color: theme.palette.text.primary }}>
                      Gallery Representation
                    </Typography>
                    {cvData.galleryRepresentation.map((gallery, index) => (
                      <Typography key={index} variant="body1" sx={{ color: theme.palette.text.secondary, mb: 1 }}>
                        {renderSafeMarkdown(gallery.name)}
                      </Typography>
                    ))}
                  </Box>
                  <Divider sx={{ my: 3 }} />
                </>
              )}
            </>
          )}

          {/* Contact + Social Media are rendered as brand icons in the header row (#1090, #1091) */}

          {/* Additional Info */}
          {cvData.additionalInfo && (
            <Box>
              <Typography variant="h5" sx={{ mb: 2, color: theme.palette.text.primary }}>
                Additional Information
              </Typography>
              <Typography variant="body1" sx={{ color: theme.palette.text.secondary, whiteSpace: 'pre-wrap' }}>
                {renderSafeMarkdown(cvData.additionalInfo)}
              </Typography>
            </Box>
          )}

          {/* No public content message */}
          {!hasPublicContent && (
            <Box>
              <Typography variant="body1" sx={{ color: theme.palette.text.secondary, fontStyle: 'italic' }}>
                This user has not made any CV information public.
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Share Modal */}
      <Dialog open={shareModalOpen} onClose={() => setShareModalOpen(false)} maxWidth="sm" fullWidth fullScreen={fullScreenMobile}>
        <DialogTitle sx={{ position: 'relative', pr: 5 }}>
          Share Profile
          <IconButton onClick={() => setShareModalOpen(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Share Link"
            value={shareUrl}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <IconButton onClick={handleCopyLink} data-testid="copy-link-button">
                  <ContentCopy />
                </IconButton>
              ),
            }}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShareModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for copy confirmation */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Container>
  );
};
